/**
 * Marks judge checks of one trial that the reviewer thinks the judge got wrong. These
 * flags are the labels used to measure and improve the judge, so the note should say what
 * the right verdict was.
 */
import { Flag } from 'lucide-react';
import { useState } from 'react';
import Button from '../../../../components/Button';
import InlineErrorDisplay from '../../../../components/InlineErrorDisplay';
import type { StoredTrialResult } from '../../types/evalTypes';
import { formatDateTime } from '../../utils/evalFormat';

const MAX_NOTE_CHARS = 2000;

interface JudgeFlagControlProps {
  result: StoredTrialResult;
  currentUid: string | undefined;
  /** An empty `checkIds` clears the flag. Throws on failure. */
  onSave: (checkIds: string[], note: string) => Promise<void>;
}

export const JudgeFlagControl = ({ result, currentUid, onSave }: JudgeFlagControlProps) => {
  const flag = result.judgeFlag;
  const judgeChecks = result.checks.filter((check) => check.kind === 'judge');

  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(flag?.checkIds ?? []));
  const [note, setNote] = useState(flag?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (judgeChecks.length === 0) return null;

  const startEditing = () => {
    setSelected(new Set(flag?.checkIds ?? []));
    setNote(flag?.note ?? '');
    setError(null);
    setEditing(true);
  };

  const save = async (checkIds: string[]) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(checkIds, checkIds.length > 0 ? note.trim() : '');
      setEditing(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to save the flag.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (checkId: string) => {
    const next = new Set(selected);
    if (next.has(checkId)) {
      next.delete(checkId);
    } else {
      next.add(checkId);
    }
    setSelected(next);
  };

  if (!editing) {
    if (!flag) {
      return (
        <button type="button" onClick={startEditing} className="inline-flex items-center gap-1 text-xs text-gray-700 hover:text-amber-700">
          <Flag size={12} /> Judge is wrong?
        </button>
      );
    }
    return (
      <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <span className="inline-flex items-center gap-1 font-medium text-amber-900">
            <Flag size={12} /> Judge wrong on: <span className="font-mono">{flag.checkIds.join(', ')}</span>
          </span>
          <button type="button" onClick={startEditing} className="text-blue-600 hover:underline shrink-0">
            Edit
          </button>
        </div>
        {flag.note && <p className="whitespace-pre-wrap text-gray-800">{flag.note}</p>}
        <p className="text-gray-500">
          By {flag.flaggedBy === currentUid ? 'you' : flag.flaggedBy}, {formatDateTime(flag.flaggedAt)}
        </p>
      </div>
    );
  }

  return (
    <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 space-y-2">
      <p className="font-medium text-amber-900">Which judge checks are wrong?</p>
      <ul className="space-y-1">
        {judgeChecks.map((check) => (
          <li key={check.id}>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={selected.has(check.id)} disabled={saving} onChange={() => toggle(check.id)} />
              <span className="font-mono">{check.id}</span>
              <span className="text-gray-500">(judge said {check.verdict})</span>
            </label>
          </li>
        ))}
      </ul>
      <textarea
        value={note}
        maxLength={MAX_NOTE_CHARS}
        onChange={(event) => setNote(event.target.value)}
        disabled={saving}
        rows={2}
        placeholder="What is the right verdict, and why? For example: fact 2 is in the second sentence."
        className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
      />
      {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
      <div className="flex flex-wrap justify-end gap-2">
        {flag && (
          <Button variant="outline" size="sm" onClick={() => void save([])} disabled={saving}>
            Remove flag
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={() => void save([...selected])} disabled={saving || selected.size === 0} loading={saving}>
          Save flag
        </Button>
      </div>
    </div>
  );
};
